import { useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import { Status, StatusType, useChainData } from 'modules/common';
import { useTranslation } from 'modules/i18n';
import { TaskRoutesConfig } from 'modules/task';

import { GetTasksItem } from '../../actions/getTasks';
import { AddressCell } from './AddressCell';
import { AmountSpentCell } from './AmountSpentCell';
import { NameCell } from './NameCell';
import translation from './tasksTable.translation';

interface Props {
  task: GetTasksItem;
}

export function ListItem({ task }: Props): JSX.Element {
  const navigate = useNavigate();
  const { classes } = useStyles();
  const { t, keys } = useTranslation(translation);
  const {
    id,
    name,
    cronTarget: address,
    isPaused,
    amountSpent,
    chainId,
  } = task;
  const chainData = useChainData(chainId);

  return (
    <div
      className={classes.item}
      onClick={() =>
        chainData?.chainUnit &&
        navigate(
          TaskRoutesConfig.editTask.generatePath(id, chainData.chainUnit),
        )
      }
    >
      <div className={classes.itemRow}>
        <Typography
          variant="subtitle3"
          sx={theme => ({ color: theme.palette.grey[600] })}
        >
          {t(keys.name)}
        </Typography>

        {chainId && <NameCell name={name} chainId={chainId} />}
      </div>

      <div className={classes.itemRow}>
        <Typography
          variant="subtitle3"
          sx={theme => ({ color: theme.palette.grey[600] })}
        >
          {t(keys.status)}
        </Typography>

        <Status isError={isPaused} type={StatusType.Task} />
      </div>

      {address && (
        <div className={classes.itemRow}>
          <Typography
            variant="subtitle3"
            sx={theme => ({ color: theme.palette.grey[600] })}
          >
            {t(keys.address)}
          </Typography>

          <AddressCell address={address} />
        </div>
      )}

      <div className={classes.itemRow}>
        <Typography
          variant="subtitle3"
          sx={theme => ({ color: theme.palette.grey[600] })}
        >
          {t(keys.amountSpent)}
        </Typography>

        <AmountSpentCell
          amount={amountSpent}
          unit={chainData?.tokenName ?? ''}
        />
      </div>
    </div>
  );
}

const useStyles = makeStyles()(theme => ({
  item: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(6),
    padding: theme.spacing(4),
    borderRadius: 16,
    backgroundColor: theme.palette.background.paper,
    cursor: 'pointer',
  },

  itemRow: {
    display: 'flex',
    justifyContent: 'space-between',
  },
}));
